// SupplyMatchingSummaryTab.js - This file defines the SupplyMatchingSummaryTab component, which is specific to the supply matching functionality within the modal. 
// It handles:displaying specific data: It formats and shows data related to supply matching, such as supplier details, part numbers, and quantities.
// User Interaction for Supply Matching: It includes UI elements like checkboxes and buttons that users interact with to select supplies and generate RFQs
// (Request for Quotation).
// Triggering Business Logic: It has buttons or other interactive elements that, when triggered, can execute functions passed down as props. 
// this might include generating and sending RFQs.
// InputSection (the partent of OverlayModal) is managing various states (including states for 'supplyMatchingSummary') and passing them down to OverlayModal 
// then OverlayModal passes the needed states to SupplyMatchingSummaryTab.

// Import the INTERNAL_KEYS from the shared file
import INTERNAL_KEYS from '../../constants/internalKeys';
import React, { useState, useEffect, useCallback } from 'react';

const Supply_matching_Summary_Tab = ({ 
    summaryData,
    fullMatches,
    setRfqEmailData,
    setIsWaitingForResponse,
    isWaitingForResponse,
    setActiveTab,
}) => {
    const [selectedSuppliersForRFQ, setSelectedSuppliersForRFQ] = useState([]);

    useEffect(() => {
        // Enhanced logging
        console.log("SupplyMatchingSummaryTab - Component Mounted/Updated");
        console.log("summaryData:", {
            type: typeof summaryData,
            isArray: Array.isArray(summaryData),
            length: summaryData?.length,
            data: summaryData
        });
        console.log("fullMatches:", {
            type: typeof fullMatches,
            isArray: Array.isArray(fullMatches),
            length: fullMatches?.length,
            data: fullMatches
        });
    }, [summaryData, fullMatches]);

    const handleCheckboxChange = useCallback((item, isChecked) => {
        setSelectedSuppliersForRFQ(prev => {
            const newSelection = isChecked 
                ? [...prev, item.docId] 
                : prev.filter(id => id !== item.docId);
            console.log("Updated selectedSuppliersForRFQ:", newSelection);
            return newSelection;
        });
    }, []);

    const handleSelectAllChange = useCallback((isChecked) => {
        setSelectedSuppliersForRFQ(isChecked ? summaryData.map(item => item.docId) : []);
    }, [summaryData]);

    const handleGenerateRFQ = useCallback(async () => {
        if (!fullMatches || !summaryData) {
            console.error("Full matches or summary data is not available");
            return;
        }

        const selectedItems = selectedSuppliersForRFQ.map(docId => {
            const summaryItem = summaryData.find(item => item.docId === docId);
            const fullMatchItem = fullMatches?.find(item => item.docId === docId);
            if (!fullMatchItem || !summaryItem) {
                console.warn(`Data not found for docId: ${docId}`);
                return null;
            }
            const mergedItem = { ...summaryItem, ...fullMatchItem };
            console.log("Merged item for RFQ:", mergedItem);
            return mergedItem;
        }).filter(item => item !== null);

        console.log("Selected items for RFQ:", selectedItems);

        if (selectedItems.length === 0) {
            console.error("No valid items selected for RFQ generation");
            return;
        }

        setIsWaitingForResponse(true);
        try {
            const response = await fetch('https://us-central1-maia-tech.cloudfunctions.net/RFQEmailAutomation', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ matches: selectedItems })
            });

            if (!response.ok) {
                throw new Error('Failed to fetch RFQ emails: ' + response.statusText);
            }

            const responseData = await response.json();
            console.log("Response from RFQEmailAutomation:", JSON.stringify(responseData, null, 2));
            setRfqEmailData(responseData);
            setActiveTab('rfqEmails');
            
            setSelectedSuppliersForRFQ([]);
        } catch (error) {
            console.error("Error in generating RFQ:", error);
        } finally {
            setIsWaitingForResponse(false);
        }
    }, [summaryData, fullMatches, selectedSuppliersForRFQ, setRfqEmailData, setIsWaitingForResponse, setActiveTab]);

    if (!summaryData || summaryData.length === 0) {
        return <div>No supply matching data available</div>;
    }

    return (
        <div className="overlay-modal">
            <h3>Supply Matching Summary ({summaryData.length} Matches)</h3>
            <div className="modal-body">
                <table className="supply-matching-table">
                    <thead>
                        <tr>
                            <th>Supplier</th>
                            <th>Customer MPN</th>
                            <th>Manufacturer</th>
                            <th>Target</th>
                            <th>Demand QTY</th>
                            <th>Total Opportunity</th>
                            <th>Supplier Price</th>
                            <th>Supplier QTY</th>
                            <th>Profit Potential</th>
                            <th>Source</th>
                            <th>Contact</th>
                            <th>Email</th>
                            <th>
                                <input
                                    type="checkbox"
                                    onChange={(e) => handleSelectAllChange(e.target.checked)}
                                    checked={selectedSuppliersForRFQ.length === summaryData.length}
                                />
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {summaryData.map((item) => (
                            <tr key={item.docId}>
                                <td>{item[INTERNAL_KEYS['supplier_name']] ?? 'No Supplier Available'}</td>
                                <td>{item[INTERNAL_KEYS['customer_mpn']] ?? 'No Customer MPN Available'}</td>
                                <td>{item[INTERNAL_KEYS['customer_mfr']] ?? 'No Manufacturer Available'}</td>
                                <td>{item[`formatted_${INTERNAL_KEYS['customer_target']}`] ?? 'No Target Available'}</td>  
                                <td>{item[`formatted_${INTERNAL_KEYS['demand_qty']}`] ?? 'No Demand QTY Available'}</td>  
                                <td>{item[`formatted_${INTERNAL_KEYS['total_opportunity']}`] ?? 'No Total Opportunity Available'}</td> 
                                <td>{item[`formatted_${INTERNAL_KEYS['supplier_price']}`] ?? 'No Supplier Price Available'}</td>  
                                <td>{item[`formatted_${INTERNAL_KEYS['supplier_qty']}`] ?? 'No Supply QTY Available'}</td>  
                                <td>{item[`formatted_${INTERNAL_KEYS['profit_potential']}`] ?? 'No Profit Potential Available'}</td>  
                                <td>{item[INTERNAL_KEYS['supplier_source']] ?? 'No Source Available'}</td>
                                <td>{item[INTERNAL_KEYS['supplier_contact']] ?? 'No Contact Available'}</td>
                                <td>{item[INTERNAL_KEYS['supplier_email']] ?? 'No Email Available'}</td>
                                <td>
                                    <input 
                                        type="checkbox"
                                        onChange={(e) => handleCheckboxChange(item, e.target.checked)}
                                        checked={selectedSuppliersForRFQ.includes(item.docId)}
                                    />
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <div className="generate-rfq-button-container">
                <button 
                    className="generate-rfq-button" 
                    onClick={handleGenerateRFQ} 
                    disabled={isWaitingForResponse || selectedSuppliersForRFQ.length === 0 || !fullMatches}>
                    Generate RFQ
                </button>
            </div>
        </div>
    );
};    

export default React.memo(Supply_matching_Summary_Tab);